import Typography from '@material-ui/core/Typography'
import { GatsbyImage, getImage, withArtDirection } from 'gatsby-plugin-image'
import React from 'react'

import { buildImageDataLike } from '../../utils/helpers'
import { TTrainStopSlide } from '../TrainstopSection'
import LinkCTA from '../UI/LinkCTA'
import Wrapper from '../UI/Wrapper'
import { BackgroundGradient, ContentContainer, SlideContainer } from './styles'

interface TrainstopImageSlideProps {
  isActive: boolean
  activeSlide: number
  ctaText?: string
  initialSlide?: boolean
  isHeroTrainStop?: 'Yes' | 'No' | undefined
  className?: string
  slide: TTrainStopSlide
}

const TrainstopImageSlide = ({
  slide,
  isActive,
  ctaText,
  isHeroTrainStop,
  activeSlide,
  initialSlide,
  className = '',
}: TrainstopImageSlideProps) => {
  const featureImage = getImage(
    buildImageDataLike(slide.id, slide.feature_image?.imgixImage),
  )
  const desktopImage = getImage(
    buildImageDataLike(slide.id, slide.background_image?.imgixImage),
  )
  const mobileImage = getImage(
    buildImageDataLike(slide.id, slide.background_image_mobile?.imgixImage),
  )

  const images =
    desktopImage && mobileImage
      ? withArtDirection(desktopImage, [
          {
            media: '(max-width: 580px)',
            image: mobileImage,
          },
        ])
      : undefined

  if (isHeroTrainStop === 'Yes') {
    return (
      <SlideContainer
        className={`${className} slideContainer ${
          isActive ? 'slideActive' : ''
        } ${slide.theme?.toLowerCase()} ${
          initialSlide ? 'firstHeroSlide' : ''
        }`}
      >
        <ContentContainer className="heroTrainstopContent">
          <Wrapper>
            <div className="heroGrid">
              <div className="titleContainer" />
              <div className="contentGrid">
                {featureImage ? (
                  <div
                    className={`featureImageContainer heroSlideImage ${
                      activeSlide === -1 ? 'heroSlideInitialImage' : ''
                    }`}
                  >
                    <GatsbyImage
                      alt={slide?.feature_image?.description || ''}
                      image={featureImage}
                    />
                  </div>
                ) : null}
                {!initialSlide ? (
                  <div className="contentContainer">
                    <Typography
                      className={`heroSlideSubhead ${slide.theme?.toLowerCase()}`}
                      component="h3"
                      variant="h3"
                    >
                      {slide.headline}
                    </Typography>
                    <Typography
                      className={`heroSlideBody ${slide.theme?.toLowerCase()}`}
                      component="p"
                      variant="h4"
                    >
                      {slide.body}
                    </Typography>
                    {slide.cta?.[0] ? (
                      <div className="heroSlideCtaContainer">
                        <LinkCTA
                          arrow
                          link={slide.cta[0]?.link?.[0]}
                          variant={slide.theme === 'Dark' ? 'ghost' : 'primary'}
                        >
                          {slide.cta?.[0]?.text}
                          <span className="sr-only"> - {slide.nav_label}</span>
                        </LinkCTA>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
              {activeSlide === -1 && (
                <Typography
                  className="heroCaption"
                  component="p"
                  variant="caption"
                >
                  {ctaText}
                </Typography>
              )}
            </div>
          </Wrapper>
        </ContentContainer>
      </SlideContainer>
    )
  }

  return (
    <SlideContainer
      className={`slideContainer ${className} ${
        slide.impact_statistic ? 'impactSlide' : ''
      } ${isActive ? 'slideActive' : ''} ${slide.theme?.toLowerCase()}`}
    >
      <ContentContainer
        className={slide.impact_statistic ? 'impactContent' : ''}
      >
        <Wrapper className={slide.impact_statistic ? 'impactWrapper' : ''}>
          <div
            className={
              slide.impact_statistic || featureImage ? 'impactGrid' : ''
            }
          >
            <div className="contentContainer">
              <Typography
                className="slideSubhead"
                component="h3"
                variant="caption"
              >
                {slide.headline}
              </Typography>
              <Typography className="slideBody" component="p" variant="h3">
                {slide.body}
              </Typography>
              {slide.cta?.[0] ? (
                <div>
                  <LinkCTA
                    arrow
                    link={slide.cta[0]?.link?.[0]}
                    variant={slide.theme === 'Dark' ? 'ghost' : 'primary'}
                  >
                    {slide.cta?.[0]?.text}
                    <span className="sr-only"> - {slide.nav_label}</span>
                  </LinkCTA>
                </div>
              ) : null}
            </div>
            {slide.impact_statistic ? (
              <div className="impactStatContainer">
                <p className="stat">{slide.impact_statistic}</p>
              </div>
            ) : null}
            {featureImage ? (
              <div className="featureImageContainer">
                <GatsbyImage
                  alt={slide?.feature_image?.description || ''}
                  image={featureImage}
                />
              </div>
            ) : null}
          </div>
        </Wrapper>
      </ContentContainer>
      {desktopImage ? (
        <div className="backgroundImage">
          <GatsbyImage
            alt={slide?.background_image?.description || ''}
            className={images ? 'hasMobileImage' : 'noMobileImage'}
            image={images || desktopImage}
          />
        </div>
      ) : null}
      <BackgroundGradient
        className={slide.theme === 'Dark' ? 'darkTheme' : 'lightTheme'}
      />
    </SlideContainer>
  )
}

export default TrainstopImageSlide
